import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Stack, Grid, Chip, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import { useOptionsStore } from '../../store/useOptionsStore';
import RadarIcon from '@mui/icons-material/Radar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface StrategyEvaluation {
  id: string;
  name: string;
  score: number;
  status: 'ELIGIBLE' | 'WATCH' | 'BLOCKED';
  summaryReason: string;
  reasons: Array<{ text: string; passed: boolean }>;
}

const DEFAULT_STRATEGIES: StrategyEvaluation[] = [
  {
    id: 'SHORT_STRANGLE',
    name: 'Short Strangle (16Δ / 16Δ)',
    score: 84,
    status: 'ELIGIBLE',
    summaryReason: 'Consolidating range + elevated IV Rank (74)',
    reasons: [
      { text: 'ADX < 25 indicates low directional trend strength', passed: true },
      { text: 'NIFTY spot is trading inside opening range boundaries', passed: true },
      { text: 'IV Rank is 74 (above minimum 20 threshold)', passed: true },
      { text: 'VRP (Variance Risk Premium) > 1.0 confirms option overpricing', passed: true },
      { text: '16Δ strike liquidity & tight bid-ask spreads available', passed: true },
      { text: 'Event risk is classified as LOW (No scheduled budget/RBI meetings)', passed: true },
    ],
  },
  {
    id: 'IRON_CONDOR',
    name: 'Iron Condor (Defined Risk)',
    score: 79,
    status: 'ELIGIBLE',
    summaryReason: 'Defined risk profile with favorable margin benefit',
    reasons: [
      { text: 'Defined max loss provides overnight gap protection', passed: true },
      { text: 'Net premium capture satisfies >10% capital return target', passed: true },
      { text: 'Wing strikes available at 4-strike width offset', passed: true },
      { text: 'Slightly higher transaction cost overhead vs naked strangle', passed: false },
    ],
  },
  {
    id: 'SHORT_STRADDLE',
    name: 'Short Straddle (ATM 50Δ)',
    score: 42,
    status: 'WATCH',
    summaryReason: 'Gamma risk elevated; ATM delta sensitivity high',
    reasons: [
      { text: 'ATM premium capture satisfies minimum requirement', passed: true },
      { text: 'Gamma risk exceeds permissible session tolerance (-0.0035 > -0.0025)', passed: false },
      { text: 'Whipsaw risk around ATM level during early session', passed: false },
    ],
  },
  {
    id: 'LONG_STRANGLE',
    name: 'Long Strangle (Debit Wings)',
    score: 18,
    status: 'BLOCKED',
    summaryReason: 'IV too elevated; Theta bleed prohibitive for buyers',
    reasons: [
      { text: 'IV Rank 74 implies high volatility crush risk on long options', passed: false },
      { text: 'Expected daily Theta decay is negative (-₹2,800/d)', passed: false },
      { text: 'No major catalyst or breakout volume detected', passed: false },
    ],
  },
];

const StrategyRadarPage: React.FC = () => {
  const [strategiesList, setStrategiesList] = useState<StrategyEvaluation[]>(DEFAULT_STRATEGIES);
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyEvaluation>(DEFAULT_STRATEGIES[0]);
  const { noTradeDecision, fetchNoTradeDecision, fetchSpotData } = useOptionsStore();

  const fetchLiveStrategies = async () => {
    try {
      fetchNoTradeDecision();
      fetchSpotData();
      const res = await optionsService.getStrategies('NIFTY');
      if (res && Array.isArray(res.signals) && res.signals.length > 0) {
        const mapped: StrategyEvaluation[] = res.signals.map((s: any) => {
          const rawScore = typeof s.confidence === 'number' ? s.confidence : 75;
          const normalizedScore = rawScore <= 1 ? Math.round(rawScore * 100) : Math.round(rawScore);
          const isEligible = s.eligible === true || s.action === 'ENTER';
          const reasonList = Array.isArray(s.reasonCodes) && s.reasonCodes.length > 0
            ? s.reasonCodes
            : Array.isArray(s.reasons) && s.reasons.length > 0
            ? s.reasons
            : [s.reason || (isEligible ? 'Passed volatility and risk filters' : 'Blocked by regime/IV gates')];

          return {
            id: s.strategy || s.strategyName || s.id,
            name: (s.strategy || s.strategyName || s.id || 'STRATEGY').replace(/_/g, ' '),
            score: normalizedScore,
            status: isEligible ? 'ELIGIBLE' : s.action === 'ADJUST' ? 'WATCH' : 'BLOCKED',
            summaryReason: reasonList[0] || s.reason || 'Evaluated against live regime and risk parameters',
            reasons: reasonList.map((r: string) => ({ text: r, passed: isEligible })),
          };
        });
        setStrategiesList(mapped);
        if (mapped.length > 0) {
          setSelectedStrategy(mapped[0]);
        }
      }
    } catch {
      // Retain default
    }
  };

  useEffect(() => {
    fetchLiveStrategies();
    const interval = setInterval(fetchLiveStrategies, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: StrategyEvaluation['status']) => {
    switch (status) {
      case 'ELIGIBLE': return { bg: 'rgba(34, 197, 94, 0.12)', text: '#16A34A', label: '🟢 ELIGIBLE' };
      case 'WATCH': return { bg: 'rgba(245, 158, 11, 0.12)', text: '#D97706', label: '🟡 WATCH' };
      case 'BLOCKED': return { bg: 'rgba(239, 68, 68, 0.12)', text: '#DC2626', label: '🔴 BLOCKED' };
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Grid container spacing={2.5}>
        {/* Strategy Radar List */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <RadarIcon sx={{ color: '#0284C7' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                STRATEGY OPPORTUNITY RADAR
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              {strategiesList.map((strat) => {
                const isSelected = strat.id === selectedStrategy.id;
                const statusMeta = getStatusColor(strat.status);

                return (
                  <Paper
                    key={strat.id}
                    elevation={0}
                    onClick={() => setSelectedStrategy(strat)}
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      border: isSelected ? '2px solid #0284C7' : '1px solid #E2E8F0',
                      bgcolor: isSelected ? '#F0F9FF' : '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: '#F8FAFC' },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                          {strat.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.3 }}>
                          {strat.summaryReason}
                        </Typography>
                      </Box>

                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            SCORE
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: strat.score > 70 ? '#16A34A' : '#DC2626' }}>
                            {strat.score}
                          </Typography>
                        </Box>
                        <Chip size="small" label={statusMeta.label} sx={{ bgcolor: statusMeta.bg, color: statusMeta.text, fontWeight: 700 }} />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        {/* Explainability Panel ("WHY?") */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <HelpOutlineIcon sx={{ color: '#0284C7' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                SIGNAL EXPLAINABILITY ("WHY?")
              </Typography>
            </Stack>

            <Typography variant="subtitle2" sx={{ color: '#0284C7', fontWeight: 700, mb: 2 }}>
              {selectedStrategy.name} (Score: {selectedStrategy.score}/100)
            </Typography>

            <List disablePadding>
              {selectedStrategy.reasons.map((r, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.8, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>
                    {r.passed ? <CheckCircleIcon sx={{ fontSize: 18, color: '#16A34A' }} /> : <CancelIcon sx={{ fontSize: 18, color: '#DC2626' }} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={r.text}
                    primaryTypographyProps={{
                      fontSize: '0.82rem',
                      color: r.passed ? '#1E293B' : '#64748B',
                      fontWeight: r.passed ? 500 : 400,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StrategyRadarPage;
